import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { FormSubmitButton, FormCancelButton, ErrorAlert, FormField, WarningAlert, withModalMount } from '@petseo/shared';
import { FormModal } from '@/components/FormModal';
import { employeeSchema, type EmployeeFormData } from '@/schemas/employee.schema';
import { useCreateEmployee, useUpdateEmployee } from '../../hooks';
import type { ServiceVendorId, EmployeeDto } from '@/services/api/petseo.client.wrapper';
import { createEmployeeId } from '@/services/api/petseo.client.wrapper';

interface EmployeeFormProps {
  serviceVendorId: ServiceVendorId;
  employee?: EmployeeDto;
  isOpen: boolean;
  onClose: () => void;
}

function EmployeeFormContent({ serviceVendorId, employee, isOpen, onClose }: EmployeeFormProps) {
  const isEditing = !!employee;

  const navigate = useNavigate();

  const createEmployee = useCreateEmployee();
  const updateEmployee = useUpdateEmployee();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<EmployeeFormData>({
    resolver: zodResolver(employeeSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      position: '',
    },
  });

  useEffect(() => {
    reset({
      firstName: employee?.firstName ?? '',
      lastName: employee?.lastName ?? '',
      position: employee?.position ?? '',
    });
  }, [isOpen, employee, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: EmployeeFormData) => {
    if (isEditing && employee?.id) {
      await updateEmployee.mutateAsync({
        serviceVendorId,
        serviceVendorEmployeeId: createEmployeeId(employee.id),
        data,
      });
    } else {
      await createEmployee.mutateAsync({
        serviceVendorId,
        data,
      });
    }
    handleClose();
  };

  const isSubmitting = createEmployee.isPending || updateEmployee.isPending;
  const error = createEmployee.error || updateEmployee.error;
  const showLimitWarning = createEmployee.isEmployeeLimitReached;

  const handleUpgradeClick = () => {
    handleClose();
    navigate('/subscription');
  };

  return (
    <FormModal isOpen={isOpen} onClose={handleClose} title={isEditing ? 'Edytuj pracownika' : 'Dodaj pracownika'} size="lg">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="modal-body">
          {showLimitWarning ? (
            <WarningAlert
              title="Osiągnięto limit pracowników"
              message="Twój aktualny plan nie pozwala na dodanie kolejnego pracownika. Przejdź do subskrypcji, aby zmienić plan."
              action={{ label: 'Zarządzaj subskrypcją', onClick: handleUpgradeClick }}
              className="mb-3"
            />
          ) : (
            error && <ErrorAlert apiError={error} />
          )}

          <FormField control={control} name="firstName" label="Imię" placeholder="np. Jan" required error={errors.firstName} />
          <FormField control={control} name="lastName" label="Nazwisko" placeholder="np. Kowalski" required error={errors.lastName} />
          <FormField control={control} name="position" label="Stanowisko" placeholder="np. Groomer" error={errors.position} />
        </div>

        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <FormCancelButton onClick={handleClose} disabled={isSubmitting} />
          <FormSubmitButton isLoading={isSubmitting} disabled={isSubmitting} className="btn btn-primary">
            {isSubmitting ? 'Zapisywanie...' : 'Zapisz'}
          </FormSubmitButton>
        </div>
      </form>
    </FormModal>
  );
}

export const EmployeeForm = withModalMount(EmployeeFormContent);
